import React from 'react';

const ContractDetailModal = ({ isOpen, onClose, data, onRemind, onDownload }) => {
    if (!isOpen || !data) return null;

    const { contract, signatureStatus } = data;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-2xl w-[700px] max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">{contract.contract_name}</h2>
                        <p className="text-xs text-gray-500 font-medium">ID: {contract.id} • Created: {new Date(contract.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-2 gap-6 mb-8">
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 tracking-wider mb-3">Contract Info</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-bold text-gray-500">Subject</label>
                                    <p className="text-sm font-semibold text-gray-800">{contract.subject}</p>
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-gray-500">Signature Request ID</label>
                                    <p className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded inline-block">
                                        {contract.signature_request_id || 'N/A'}
                                    </p>
                                </div>
                                {contract.remark && (
                                    <div>
                                        <label className="text-[11px] font-bold text-gray-500">Remark</label>
                                        <p className="text-sm text-gray-700 italic">"{contract.remark}"</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 tracking-wider mb-3">Overall Status</h3>
                            <div className="flex items-center gap-3">
                                <div className={`px-4 py-2 rounded-full font-bold text-sm ${signatureStatus?.is_complete ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {signatureStatus?.is_complete ? 'COMPLETED' : 'IN PROGRESS'}
                                </div>
                                <button 
                                    onClick={() => onDownload(contract.id)}
                                    className="px-4 py-2 bg-[#ffeb00] text-black font-bold text-xs rounded hover:bg-[#ffd200] transition-colors flex items-center gap-2 shadow-sm"
                                >
                                    <i className="fas fa-download"></i>
                                    Download PDF
                                </button>
                            </div>
                        </div>
                    </div>

                    <h3 className="text-xs font-bold text-gray-400 tracking-wider mb-3">Signers & Status</h3>
                    <div className="border border-gray-100 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 text-left">
                                    <th className="px-4 py-3 font-bold text-gray-600">Signer Name</th>
                                    <th className="px-4 py-3 font-bold text-gray-600">Email</th>
                                    <th className="px-4 py-3 font-bold text-gray-600">Status</th>
                                    <th className="px-4 py-3 font-bold text-center text-gray-600">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {signatureStatus?.signatures?.map((sig, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 font-medium text-gray-800">{sig.signer_name}</td>
                                        <td className="px-4 py-3 text-gray-600">{sig.signer_email_address}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${
                                                sig.status === 'signed' ? 'bg-green-100 text-green-600' : 
                                                sig.status === 'declined' ? 'bg-red-100 text-red-600' : 
                                                'bg-yellow-100 text-yellow-600'
                                            }`}>
                                                {sig.status.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {sig.status !== 'signed' && (
                                                <button 
                                                    onClick={() => onRemind(contract.id, sig.signer_email_address)}
                                                    className="text-[#ffeb00] hover:text-black transition-colors"
                                                    title="Send Reminder"
                                                >
                                                    <i className="fas fa-paper-plane"></i>
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {(!signatureStatus?.signatures || signatureStatus.signatures.length === 0) && (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-6 text-center text-gray-400 italic">No signer data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 text-right">
                    <button 
                        onClick={onClose}
                        className="px-6 py-2 bg-white border border-gray-300 text-gray-700 font-bold rounded shadow-sm hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractDetailModal;
